/**
 * Resolve the active tenant scope (slug).
 *
 * - Localhost / IP / bare host → `VITE_TENANT_SLUG` (default `vipso`)
 * - Deployed subdomain → first label, with env suffixes stripped so the same
 *   tenant works across environments:
 *     `omtrading.spurnest.com`     → `omtrading`
 *     `omtrading-uat.spurnest.com` → `omtrading`
 *     `omtrading-dev.spurnest.com` → `omtrading`
 *     `vipso-uat.spurnest.com`     → `vipso`
 * - Apex / www (no tenant label) → `VITE_TENANT_SLUG` fallback
 */

/** Host-label suffixes that mark environment, not tenant identity. */
export const TENANT_ENV_SUFFIXES = ['uat', 'dev', 'staging', 'test', 'local'] as const;

/**
 * Strip a trailing `-{env}` from a subdomain label.
 * Only strips known env tokens so a real slug like `omtrading` is unchanged.
 */
export function stripTenantEnvSuffix(subdomain: string): string {
  const lower = subdomain.trim().toLowerCase();
  if (!lower) return lower;

  for (const env of TENANT_ENV_SUFFIXES) {
    const suffix = `-${env}`;
    if (lower.endsWith(suffix) && lower.length > suffix.length) {
      return lower.slice(0, -suffix.length);
    }
  }
  return lower;
}

export function resolveTenantSlug(): string {
  const configured = String(import.meta.env.VITE_TENANT_SLUG || 'vipso')
    .trim()
    .toLowerCase();

  if (typeof window === 'undefined') return configured || 'vipso';

  const hostname = window.location.hostname.toLowerCase();

  // Local / direct IP — always use configured tenant
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '[::1]' ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return configured || 'vipso';
  }

  // tenant[.env].domain.tld → tenant (e.g. omtrading-uat.spurnest.com → omtrading)
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length >= 3) {
    const subdomain = parts[0];
    if (subdomain && subdomain !== 'www' && subdomain !== 'app' && subdomain !== 'admin') {
      const slug = stripTenantEnvSuffix(subdomain);
      return slug || configured || 'vipso';
    }
  }

  // Two-label host like spurnest.com (no tenant subdomain)
  return configured || 'vipso';
}

/** True when running on localhost / loopback (uses VITE_TENANT_SLUG). */
export function isLocalTenantHost(): boolean {
  if (typeof window === 'undefined') return true;
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '[::1]' ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  );
}
